import { getGraphql } from '../../../static/graphql/getGraphql.js'

// Intercom rejects string attribute values longer than 255 characters
const INTERCOM_ATTRIBUTE_MAX_LENGTH = 255

const parseCampaignMeta = (campaignMeta) => {
  // campaignMeta normally arrives double-JSON-encoded (a JSON string of a JSON
  // string, matching how store/contracts.js parses it); tolerate single encoding
  // and malformed payloads rather than letting one bad contract break the batch
  try {
    const once = JSON.parse(campaignMeta)
    return typeof once === 'string' ? JSON.parse(once) : once
  } catch (e) {
    return null
  }
}

/**
 * Resolve a display name for a contract's fund, or null when none is safe to show.
 *
 * Falls back from the contract name to the Spanish localized name — the same
 * localization key every fund card in this app reads — because a blank English
 * name is a known state for SF-origin funds. That fallback is a symptom of an
 * upstream data defect, so it warns rather than substituting silently. Never
 * throws and never emits a blank entry.
 */
export const resolveFundName = (contract) => {
  const name = (contract?.name || '').trim()
  if (name) {
    return name
  }
  const meta = parseCampaignMeta(contract?.campaignMeta)
  const esName = (meta?.localization?.ES?.name || '').trim()
  if (esName) {
    console.warn(
      'Intercom Funds attribute: fund has no English name, using its Spanish name'
    )
    return esName
  }
  return null
}

/**
 * Join unique fund names into an Intercom-safe string, never above 255 characters.
 *
 * When the full list does not fit, the value degrades to the names that do fit plus
 * a "+K more" suffix; a single name too long on its own is truncated with an
 * ellipsis rather than replaced, so Support always sees fund-identifying text.
 */
export const buildFundsAttribute = (names) => {
  const unique = [...new Set(names)]
  const full = unique.join(', ')
  if (full.length <= INTERCOM_ATTRIBUTE_MAX_LENGTH) {
    return full
  }
  for (let kept = unique.length - 1; kept > 0; kept--) {
    const candidate = `${unique.slice(0, kept).join(', ')} +${
      unique.length - kept
    } more`
    if (candidate.length <= INTERCOM_ATTRIBUTE_MAX_LENGTH) {
      return candidate
    }
  }
  const suffix = unique.length > 1 ? ` +${unique.length - 1} more` : ''
  const room = INTERCOM_ATTRIBUTE_MAX_LENGTH - suffix.length - 1
  return `${unique[0].slice(0, room)}…${suffix}`
}

/**
 * Fetch the fund names of the member's contracts, self and family.
 *
 * Uses a lean names-only variant of the contracts query, since this runs on every
 * authed page load and needs none of the payment detail the full query carries.
 * All contracts are included regardless of status — past (terminated/fulfilled)
 * funds are deliberately part of the Support context, pending the scope decision
 * on the ticket. GraphQL errors and a missing data envelope throw instead of
 * returning an empty list, so a failed fetch can never masquerade as "no
 * enrollments". Contracts that resolve to no safe display name are skipped with
 * a console warning — an upstream data defect worth keeping visible.
 */
export const fetchFundNames = async (axios) => {
  const query = await getGraphql('my-contract-fund-names.graphql')
  const response = await axios.$post('/GRAPHQL_URL/', { query })
  if (response.errors?.length) {
    throw new Error(response.errors[0].message || 'fund names query failed')
  }
  const relationships = response.data?.me?.relationships
  if (!relationships) {
    throw new Error('fund names query returned no member data')
  }
  const selfContracts = relationships.myself?.[0]?.contracts || []
  const familyContracts = (relationships.memberOf || []).reduce(
    (all, family) => all.concat(family.contracts || []),
    []
  )
  const contracts = [...selfContracts, ...familyContracts]
  const names = contracts.map(resolveFundName).filter(Boolean)
  const skipped = contracts.length - names.length
  if (skipped) {
    console.warn(
      `Intercom Funds attribute: ${skipped} contract(s) had no resolvable fund name`
    )
  }
  return names
}
